

import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config";
import { Modal } from "flowbite-react";
import { getTranslation } from "../../utils";
import { FiMenu } from "react-icons/fi";

const MenuDigital = () => {
const { cliente_id: paramClienteId } = useParams(); // 📌 Obtener `cliente_id` desde la URL
  const [cliente_id, setClienteId] = useState(paramClienteId || "");
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState(() => localStorage.getItem("token") || "");
  const [usuarioId, setUsuarioId] = useState(() => localStorage.getItem("usuario_id") || "");
  const [view, setView] = useState("categorias");
  const [mainCategories, setMainCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [configuraciones, setConfiguraciones] = useState({});
  const [popup, setPopup] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(true);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState("");
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [usuarioAutenticado, setUsuarioAutenticado] = useState(false);
  const { usuario_id } = useParams();
const navigate = useNavigate();
const [language, setLanguage] = useState(() => {
  return localStorage.getItem("language") || "es"; // 🔥 Recupera el idioma guardado o usa "es" por defecto
});


const defaultImage = cliente_id
    ? `static/cliente_${cliente_id}/products/default.png`
    : "static/default.png";

// ✅ **1️⃣ Sincronizar `cliente_id` con la URL (path o query)**
useEffect(() => {
    const urlClienteId = paramClienteId || searchParams.get("menu");

    if (!urlClienteId) {
        return;
    }

    if (urlClienteId !== cliente_id) {
        console.log("🔄 Actualizando cliente_id desde URL:", urlClienteId);
        setClienteId(urlClienteId);
        localStorage.setItem("cliente_id", urlClienteId);
    }
}, [paramClienteId, searchParams, cliente_id]);

// ✅ **Resetear estado cuando cambia el cliente_id**
useEffect(() => {
    if (!cliente_id) return;
    setView("categorias");
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setProducts([]);
    setSubcategories([]);
    setMainCategories([]);
    setMessage("");
}, [cliente_id]);



// ✅ **2️⃣ Cargar datos solo cuando `cliente_id` cambia**
useEffect(() => {
    if (!cliente_id) {
        console.error("❌ Error: cliente_id no está definido.");
        setMessage("Error: cliente_id no está disponible.");
        setLoading(false);
        return;
    }

    console.log("📱 Cliente ID:", cliente_id);

    // ✅ Guardar `cliente_id` en localStorage si aún no está
    if (localStorage.getItem("cliente_id") !== cliente_id) {
        localStorage.setItem("cliente_id", cliente_id);
    }

    setLoading(true);
    fetchConfiguraciones();
    fetchMainCategories();
    fetchPopup();
}, [cliente_id]);


// ✅ **3️⃣ Manejo de autenticación y menú en tiempo real**
useEffect(() => {
    const checkAuth = () => {
        const token = localStorage.getItem("token");
        const usuarioId = localStorage.getItem("usuario_id");
        console.log("🔍 Verificando autenticación:", { token, usuarioId });

        if (token && usuarioId) {
            setUsuarioAutenticado(true);
        } else {
            setUsuarioAutenticado(false);
        }
    };

    checkAuth(); 
    window.addEventListener("storage", checkAuth);

    return () => window.removeEventListener("storage", checkAuth);
}, []);  // 🔥 Se ejecuta solo una vez al cargar la página



// ✅ **5️⃣ Asegurar que el menú FI se muestre correctamente**
useEffect(() => {
    if (usuarioAutenticado && cliente_id) {
        console.log("✅ Usuario autenticado, mostrando menú lateral.");
        setSidebarOpen(true);
    }
}, [usuarioAutenticado, cliente_id]); // 🔥 Se ejecuta cuando el usuario está autenticado y tiene un restaurante

const fetchConfiguraciones = async () => {
  try {
    const timestamp = new Date().getTime();
    const response = await fetch(
      `${API_BASE_URL}/configuraciones?cliente_id=${cliente_id}&v=${timestamp}`
    );

    if (!response.ok) {
      throw new Error(`Error HTTP: ${response.status}`);
    }

    const data = await response.json();
    console.log("📌 Configuraciones obtenidas:", data); // <-- 📌 Verifica si devuelve valores

    if (!data || typeof data !== "object") {
      console.error("❌ Error: La API no devolvió un objeto válido de configuraciones.");
      return;
    }

    // ✅ Generar URL del background con prevención de caché
    const bgImage = data.backgroundImage
      ? `${data.backgroundImage}?v=${timestamp}`
      : `${API_BASE_URL}/static/images/default-backg.png`;

    // ✅ Aplicar valores por defecto si no existen
    setConfiguraciones({
      ...data,
      backgroundImage: bgImage,
      logo: data.logo
        ? `${data.logo}?v=${timestamp}`
        : `${API_BASE_URL}/static/images/default-logo.png`,
      cardColor: data.cardColor || "#f0f0f0",
      textColor: data.textColor || "#000000",
      fontFamily: data.fontFamily || "Arial",
      cardBorderRadius: data.cardBorderRadius || "10",
    });

    console.log("📌 Configuraciones aplicadas al estado:", configuraciones);
  } catch (error) {
    console.error("❌ Error al cargar configuraciones:", error);
  }
};

const fetchMainCategories = async () => {
  setLoading(true);
  try {
    const response = await fetch(
      `${API_BASE_URL}/categories?cliente_id=${cliente_id}&idioma=${language}`
    );

    if (!response.ok) {
      throw new Error(`Error HTTP: ${response.status}`);
    }

    const data = await response.json();
    const timestamp = new Date().getTime(); // Evitar caché
    const uniqueCategories = {}; // Objeto para evitar duplicados

    const updatedCategories = (data.categorias || []).reduce((acc, category) => {
      const translatedName = getTranslation(
        category.translations || {}, // 🔹 Asegurar que no sea null
        language,
        category.name
      );

      const imagePath = category.image_path
        ? category.image_path.startsWith("http")
          ? `${category.image_path}?v=${timestamp}`
          : `${API_BASE_URL}/static/cliente_${cliente_id}/${category.image_path}?v=${timestamp}`
        : `${API_BASE_URL}/static/cliente_${cliente_id}/products/default.png?v=${timestamp}`;

      // 🔹 Solo agregamos la categoría si no está en `uniqueCategories`
      if (!uniqueCategories[category.id]) {
        uniqueCategories[category.id] = {
          ...category,
          name: translatedName, // 🔥 Nombre traducido correctamente
          image_path: imagePath
        };
        acc.push(uniqueCategories[category.id]);
      }
      return acc;
    }, []);

    setMainCategories(updatedCategories);
  } catch (error) {
    console.error("Error al cargar categorías principales:", error);
    setMessage("Error al cargar categorías principales.");
  } finally {
    setLoading(false);
  }
};

const fetchSubcategories = async (categoryId) => {
  if (!cliente_id || !categoryId) {
    console.error("❌ Faltan datos para cargar subcategorías.");
    setMessage("Error: Falta información requerida para cargar subcategorías.");
    return;
  }
  setLoading(true);

  try {
    const response = await fetch(
      `${API_BASE_URL}/categories/${categoryId}/subcategories?cliente_id=${cliente_id}&idioma=${language}`
    );

    if (!response.ok) {
      throw new Error(`Error HTTP: ${response.status}. No se pudo cargar las subcategorías.`);
    }

    const text = await response.text();
    console.log("📌 Respuesta RAW de la API:", text); // 🔍 Ver la respuesta de la API

    let data;
    try {
      data = JSON.parse(text);
    } catch (error) {
      throw new Error("📌 Error al parsear JSON: " + error.message);
    }

    console.log("📌 Respuesta parseada de la API:", JSON.stringify(data, null, 2));

    if (!data || !data.subcategorias) {
      throw new Error("📌 La API no devolvió subcategorias. Verifica la estructura.");
    }

    // 🔹 Verificar si `position` existe en cada subcategoría
    data.subcategorias.forEach(subcat => {
      console.log(`📌 Subcategoría: ${subcat.name}, Position: ${subcat.position}`);
    });

    // 🔹 Asegurar que todas las subcategorías tengan posición y aplicar traducciones
    const updatedSubcategories = data.subcategorias
      .map(subcat => {
        const translatedName = getTranslation(subcat.translations, language, subcat.name);
        return {
          ...subcat,
          name: translatedName, // 🔥 Aplicar traducción
          position: subcat.position !== null && subcat.position !== undefined
            ? parseInt(subcat.position, 10)
            : Number.MAX_SAFE_INTEGER // 🔥 Evita `undefined`
        };
      })
      .sort((a, b) => a.position - b.position); // 🔥 Ordenar correctamente por `position`

    console.log("📌 Subcategorías ordenadas y traducidas:", updatedSubcategories);

    setSubcategories(updatedSubcategories);
  } catch (error) {
    console.error("❌ Error al cargar subcategorías:", error.message);
    setMessage("Error al cargar subcategorías.");
  } finally {
    setLoading(false);
  }
};

const fetchProductsBySubcategory = async (cloverCategoryId) => {
  if (!cliente_id || !cloverCategoryId) {
    console.error("Cliente ID o Category ID no definido");
    setMessage("Error: Falta información requerida para cargar productos.");
    return;
  }

  setLoading(true);
  try {
    const timestamp = new Date().getTime();
    const response = await fetch(
      `${API_BASE_URL}/productos?cliente_id=${cliente_id}&category_id=${cloverCategoryId}&idioma=${language}`
    );

    if (!response.ok) {
      throw new Error(`Error HTTP: ${response.status}. No se pudo cargar los productos.`);
    }

    const data = await response.json();
    console.log("📌 Respuesta API:", data);

    if (!data || typeof data !== "object") {
      throw new Error("❌ Respuesta inesperada de la API.");
    }

    // Asegurar que productos sea un array (API puede devolver array directo)
    const productos = Array.isArray(data?.productos)
      ? data.productos
      : Array.isArray(data)
        ? data
        : [];

    // Logo por defecto, si no hay `image_path`
    const logoAsDefault = configuraciones.logo
      ? configuraciones.logo.startsWith("http")
        ? `${configuraciones.logo}?v=${timestamp}`
        : `${API_BASE_URL}/${configuraciones.logo}?v=${timestamp}`
      : `${API_BASE_URL}/${defaultImage}?v=${timestamp}`;

    // 🔹 Aplicar traducción correctamente
    const updatedProducts = productos
      .filter((product) => product.visible)
      .map((product) => ({
        ...product,
        name: getTranslation(product.translations, language, product.name), // 🔥 Traducir nombre
        description: getTranslation(product.translations, language, product.description), // 🔥 Traducir descripción
        image_path: product.image_path
          ? product.image_path.startsWith("http")
            ? `${product.image_path}?v=${timestamp}`
            : `${API_BASE_URL}/static/${product.image_path}?v=${timestamp}`
          : logoAsDefault,
      }));

    console.log("📌 Productos final procesados:", updatedProducts);

    setProducts(updatedProducts);
  } catch (error) {
    console.error("❌ Error al cargar productos:", error.message);
    setMessage("🚀 Pendiente a nuestras promociones. ¡Pronto habrá novedades!");
  } finally {
    setLoading(false);
  }
};


const fetchPopup = async () => {
  try {
    const timestamp = new Date().getTime();
    const response = await fetch(
      `${API_BASE_URL}/popups?cliente_id=${cliente_id}&_v=${timestamp}`
    );

    if (!response.ok) {
      throw new Error("Error al obtener los popups.");
    }

    const popupData = await response.json();

    if (!popupData || !popupData.file_url) {
      console.error("Datos del popup inválidos:", popupData);
      setMessage("");
      return;
    }

    // Construir la URL final para la imagen o video
     const finalUrl = `${window.location.origin}${popupData.file_url}?v=${timestamp}`;

    const popupState = localStorage.getItem(`popup_closed_${cliente_id}`);
    setPopup({
      ...popupData,
      image_path: `${finalUrl}?v=${timestamp}`, // Cache-busting
    });
    setIsPopupOpen(popupState !== "true");

    // Función para verificar si los datos del popup han cambiado
    const popupDataHasChanged = (newData) => {
      const cachedData = localStorage.getItem(`popup_data_${cliente_id}`);
      if (cachedData) {
        return JSON.stringify(newData) !== cachedData;
      }
      localStorage.setItem(`popup_data_${cliente_id}`, JSON.stringify(newData));
      return true;
    };

    // Verificar si los datos han cambiado y actualizar el estado si es necesario
    if (popupDataHasChanged(popupData)) {
      setPopup({
        ...popupData,
        image_path: finalUrl, // Cache-busting
      });

      localStorage.setItem(`popup_closed_${cliente_id}`, "false"); // Reabrir popup si hay cambios
      setIsPopupOpen(true);
    }
  } catch (error) {
    console.error("Error al cargar el popup:", error);
    setMessage("Error al cargar el popup.");
  }
};

const closePopup = () => {
  setIsPopupOpen(false);
  localStorage.setItem(`popup_closed_${cliente_id}`, "true");
};

const reopenPopup = () => {
  setIsPopupOpen(true);
  localStorage.removeItem(`popup_closed_${cliente_id}`);
};

// Maneja el cambio de idioma
  const handleLanguageChange = (code) => {
    setLanguage(code);
    console.log(`Idioma cambiado a: ${code}`);
  };

const renderPopup = () => {
  if (!popup || !popup.file_url) {
    return null; // No hay popup disponible
  }

  if (!isPopupOpen) {
    // Renderizar botón flotante para abrir el popup
    return (
      <div
        onClick={reopenPopup}
        className="popup-minimized fixed bottom-4 right-4 bg-blue-500 text-white p-3 rounded-md"
      >
        Ver Promoción
      </div>
    );
  }

  // Renderizar el popup si está abierto
  return (
    <div className="popup-container fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="popup bg-white p-4 rounded-lg shadow-lg relative max-w-lg w-full">
        <button
          onClick={closePopup}
          className="absolute top-2 right-2 text-red-500 font-bold cursor-pointer"
        >
          ✖
        </button>
        <h2 className="text-xl font-semibold mb-4">{popup.name || "Promoción"}</h2>
        {popup.file_type.startsWith("image") ? (
          <img
            src={popup.image_path}
            alt={popup.name || "Popup"}
            className="rounded-lg shadow-md w-full h-auto object-contain"
          />
        ) : popup.file_type.startsWith("video") ? (
          <video
            src={popup.image_path}
            controls
            className="rounded-lg shadow-md w-full"
          />
        ) : (
          <p className="text-gray-600">Contenido no disponible.</p>
        )}
      </div>
    </div>
  );
};

const renderView = () => {
  if (view === "productos" && selectedSubcategory) {
    return (
      <div className="w-screen max-w-full px-0">
        {/* Botón de volver */}
        <button
          className="mb-6 px-6 py-3 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700"
          onClick={() => setView("subcategorias")}
        >
          Volver
        </button>

        {/* Título */}
        <h2 className="text-2xl font-bold mb-6 text-white text-center">Productos</h2>

        {/* Lista de productos */}
        <div className="flex flex-col gap-4">
          {products.length > 0 ? (
            products.map((product) => (
              <div
                key={product.id}
                className="border shadow-md p-4 hover:shadow-lg transition"
                style={{
                  backgroundColor: configuraciones.cardColor || "#f0f0f0",
                  borderRadius: `${configuraciones.cardBorderRadius || 10}px`, // ✅ CORREGIDO
                  color: configuraciones.textColor || "#000000",
                  fontFamily: configuraciones.fontFamily || "Arial",
                }}
              >
                {/* Imagen del producto */}
                <img
                  src={product.image_path}
                  alt={product.name || "Producto sin nombre"}
                  className="w-full h-48 object-cover rounded-md mb-4 bg-gray-200"
                />

                {/* Detalles del producto */}
                <div className="text-center">
                  <h3 className="text-lg font-semibold" style={{ color: configuraciones.textColor }}>
                    {product.name}
                  </h3> {/* ✅ CORREGIDO */}

                  <p className="font-medium" style={{ color: configuraciones.textColor }}>
                    Precio: $
                    {Number.isInteger(product.price) && product.price >= 100
                      ? (product.price / 100).toFixed(2)
                      : parseFloat(product.price).toFixed(2)}
                  </p>

                  {/* 🔥 SOLO MOSTRAR LA DESCRIPCIÓN SI EXISTE */}
                  {product.description && (
                    <p className="text-sm mt-2" style={{ color: configuraciones.textColor }}>
                      {product.description}
                    </p>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-gray-400">No se encontraron productos visibles.</p> 
          )}
        </div>
      </div>
    );
  }


// VISTA = "subcategorias"
if (view === "subcategorias" && selectedCategory) {
  console.log("📌 Subcategorías en la vista antes de renderizar:", subcategories);

  return (
    <div>
      <button
        className="mb-6 px-6 py-3 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700"
        onClick={() => setView("categorias")}
      >
        Volver a Categorías
      </button>
      <h2 className="text-2xl font-bold mb-6 text-gray-800 text-center">
        Subcategorías de {selectedCategory.name}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {subcategories.length > 0 ? (
          subcategories
            .slice() // Copia la lista para evitar modificar el estado directamente
            .map(subcat => ({
              ...subcat,
              position: Number(subcat.position) || Number.MAX_SAFE_INTEGER
            }))
            .sort((a, b) => a.position - b.position) // ✅ Orden corregido
            .map((subcategory) => (
              <div
                key={subcategory.id}
                className="border rounded-lg shadow-md p-4 bg-white cursor-pointer hover:shadow-lg transition duration-200"
                onClick={() => {
                  setSelectedSubcategory(subcategory.id);
                  setView("productos");
                  fetchProductsBySubcategory(subcategory.clover_category_id);
                }}
              >
<h3 
  style={{
    color: configuraciones.textColor || "#000000",
    fontFamily: configuraciones.fontFamily || "Arial"
  }}
  className="text-lg font-semibold"
>
  {subcategory.name}
</h3>

              </div>
            ))
        ) : (
          <p className="text-center text-gray-500 col-span-full">
            No se encontraron subcategorías.
          </p>
        )}
      </div>
    </div>
  );
}



return (
    <div className="w-full flex flex-col items-center gap-6">
      {/* 🔥 Título con estilo dinámico */}
      <h2
        className="text-2xl font-bold mb-6 text-center"
        style={{
          color: configuraciones.textColor || "#000000",
          fontFamily: configuraciones.fontFamily || "Arial",
        }}
      >
        Categorías Principales
      </h2>

      {/* 🔥 Grid de categorías */}
{/* 🔥 Grid de categorías */}
<div
  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
  style={{
    backgroundColor: "transparent", // 🔥 Se eliminó cualquier fondo blanco
  }}
>
  {mainCategories.length > 0 ? (
    mainCategories.map((category) => {
      console.log("📌 Renderizando Categoría:", category); // 🔍 Ver qué tiene cada categoría
      return (
        <div
          key={category.id}
          className="border rounded-lg shadow-md p-4 cursor-pointer hover:shadow-lg transition"
          style={{
            backgroundColor: configuraciones.cardColor || "transparent",
            borderRadius: `${configuraciones.cardBorderRadius || 10}px`,
            color: configuraciones.textColor || "#000000",
            fontFamily: configuraciones.fontFamily || "Arial",
          }}
          onClick={() => {
            setSelectedCategory(category.id);
            setView("subcategorias");
            fetchSubcategories(category.id);
          }}
        >
          {/* 🔥 Imagen de la categoría */}
          <img
            src={
              category.image_path?.startsWith("http")
                ? category.image_path
                : `${API_BASE_URL}/${category.image_path || "static/images/default-category.png"}`
            }
            alt={category.name || "Sin nombre"}
            className="w-full h-48 object-cover rounded-md mb-4 bg-gray-200"
          />
          <h3 className="text-lg font-semibold">
            {category.name && category.name.trim() !== "" ? category.name : "Sin nombre"}
          </h3>
        </div>
      );
    })
  ) : (
    <div className="text-center col-span-full">
      <img
        src={`${API_BASE_URL}/static/images/empty-state.png`}
        alt="Sin categorías"
        className="w-48 h-auto mx-auto mb-4"
      />
      <p className="text-gray-500">No se encontraron categorías principales.</p>
    </div>
       )}
    
     </div>
   
 </div>
  );
};


return (
<div className="relative min-h-screen flex flex-col items-center justify-between">
  {/* ✅ Fondo de pantalla - Ahora funciona perfectamente en móviles y escritorio */}
  {configuraciones.backgroundImage && (
    <div
      className="fixed top-0 left-0 w-full h-full"
      style={{
        backgroundImage: `url("${configuraciones.backgroundImage}")`,     
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        zIndex: -1,
        height: "100vh",
        width: "100vw",
      }}
    />
  )}

{/* ✅ Header */}
<header className="fixed top-0 left-0 w-full z-50 bg-opacity-80 bg-gray-900 flex items-center justify-between px-4 py-2 shadow-lg">
  {/* 🔹 Botón de menú solo si el usuario está autenticado */}
  {usuarioAutenticado && (
    <button
      onClick={() => setSidebarOpen(true)}
      className="text-white text-2xl md:hidden focus:outline-none"
    >
      <FiMenu />
    </button>
  )}

  {/* 🔹 Logo siempre visible */}
  <img
    src={configuraciones.logo}
    alt="Logo"
    className="w-16 h-auto object-contain rounded-md shadow-sm"
  />

  {/* 🔹 Mostrar título solo si el usuario NO está autenticado */}
  {!usuarioAutenticado && (
    <h1 className="text-white text-lg font-bold tracking-wide">
      {language === "es" ? "Menú Digital" : "Digital Menu"}
    </h1>
  )}
</header>

{/* ✅ Sidebar (Menú lateral) */}
{usuarioAutenticado && (
  <>
    {/* 🔹 Overlay oscuro para cerrar sidebar en móviles */}
    {isSidebarOpen && (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
        onClick={() => setSidebarOpen(false)}
      ></div>
    )}

    {/* 🔹 Contenedor del Sidebar */}
    <div
      className={`fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-50 transition-transform ${
        isSidebarOpen ? "translate-x-0" : "-translate-x-full"
      } md:translate-x-0 md:relative`}
    >
      {/* 🔹 Botón de cerrar Sidebar */}
      <button
        onClick={() => setSidebarOpen(false)}
        className="absolute top-2 right-2 text-gray-600 hover:text-gray-900"
      >
        ✖
      </button>

      {/* 🔹 Menú de navegación */}
      <nav className="p-4">
        <ul>
          <li>
            <a href={`/usuario/${usuario_id}/cuenta`} className="block p-2 hover:bg-gray-200">
              Cuenta
            </a>
          </li>
          <li>
            <a href={`/usuario/${cliente_id}/ofertas`} className="block p-2 hover:bg-gray-200">
              Ofertas
            </a>
          </li>
          <li>
           <a href={`/ordenar-en-linea/${cliente_id}`} className="block p-2 hover:bg-gray-200">
              Ordenar en Línea
           </a>
          </li>
          <li>
            <a href="/usuario/mis-compras" className="block p-2 hover:bg-gray-200">
              Mis Compras
            </a>
          </li>
          <li>
            <a href="/usuario/mis-puntos" className="block p-2 hover:bg-gray-200">
              Mis Puntos
            </a>
          </li>
        </ul>
      </nav>
    </div>
  </>
)}

{/* ✅ Popup flotante */}
{renderPopup()}

{/* ✅ Mensaje de error */}
{message && (
  <p className="text-red-500 text-center absolute top-20 z-40 bg-white p-2 rounded-md shadow-lg border border-red-400">
    {message}
  </p>
)}

{/* ✅ Contenido Principal */}
<main className="flex-1 w-full pt-[80px] pb-[60px] flex flex-col items-center gap-6">
  {loading ? <p className="text-gray-700">Cargando...</p> : <div className="w-full flex flex-col items-center gap-6">{renderView()}</div>}
</main>

    </div>
  );
};

export default MenuDigital; 
